import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../api/api";

const normalizeMembershipPlans = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.membershipPlans)) {
    return payload.membershipPlans;
  }

  if (Array.isArray(payload?.clients)) {
    return payload.clients;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
};

const normalizeTenants = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.tenants)) {
    return payload.tenants;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
};

const normalizeCountries = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.countries)) {
    return payload.countries;
  }

  if (Array.isArray(payload?.data?.countries)) {
    return payload.data.countries;
  }

  return [];
};

const normalizeSecurityProviders = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.securityProviders)) {
    return payload.securityProviders;
  }

  if (Array.isArray(payload?.data?.securityProviders)) {
    return payload.data.securityProviders;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
};

const initialState = {
  selectedMembershipPlan: null,
  selectedMembershipPlanRecord: null,
  membershipPlans: [],
  count: 0,
  membershipPlanDetail: {},
  tenantsData: [],
  countriesData: [],
  securityProvidersData: [],
};

export const getAllMembershipPlans = createAsyncThunk(
  "membershipPlans/getAllMembershipPlans",
  async (payload) => {
    try {
      const response = await api.membershipPlans.getAllMembershipPlans(payload);
      if (response.status === 200) {
        return response.data;
      }

      throw new Error(`Unexpected status code: ${response.status}`);
    } catch (error) {
      console.error("API call failed:", error);
      throw error;
    }
  }
);

export const getMembershipPlanTenants = createAsyncThunk(
  "membershipPlans/getMembershipPlanTenants",
  async () => {
    try {
      const response = await api.membershipPlans.getAllTenants();
      if (response.status === 201 || response.status === 200) {
        return response.data;
      }

      throw new Error(`Unexpected status code: ${response.status}`);
    } catch (error) {
      console.error("API call failed:", error);
      throw error;
    }
  }
);

export const getMembershipPlanCountries = createAsyncThunk(
  "membershipPlans/getMembershipPlanCountries",
  async (payload = {}) => {
    try {
      const response = await api.membershipPlans.getAllCountries(payload);
      if (response.status === 201 || response.status === 200) {
        return response.data;
      }

      throw new Error(`Unexpected status code: ${response.status}`);
    } catch (error) {
      console.error("API call failed:", error);
      throw error;
    }
  }
);

export const getMembershipPlanSecurityProviders = createAsyncThunk(
  "membershipPlans/getMembershipPlanSecurityProviders",
  async () => {
    try {
      const response = await api.membershipPlans.getSecurityProviders();
      if (response.status === 201 || response.status === 200) {
        return response.data;
      }

      throw new Error(`Unexpected status code: ${response.status}`);
    } catch (error) {
      console.error("API call failed:", error);
      throw error;
    }
  }
);

export const createMembershipPlan = createAsyncThunk(
  "membershipPlans/createMembershipPlan",
  async (payload) => {
    try {
      const response = await api.membershipPlans.createMembershipPlan(payload);
      if (response.status === 201) {
        return response.data;
      }

      throw new Error(`Unexpected status code: ${response.status}`);
    } catch (error) {
      console.error("API call failed:", error);
      throw error;
    }
  }
);

export const updateMembershipPlan = createAsyncThunk(
  "membershipPlans/updateMembershipPlan",
  async ({ id, payload }) => {
    try {
      const response = await api.membershipPlans.updateMembershipPlan(id, payload);
      if (response.status === 200) {
        return response.data;
      }

      throw new Error(`Unexpected status code: ${response.status}`);
    } catch (error) {
      console.error("API call failed:", error);
      throw error;
    }
  }
);

export const deleteMembershipPlan = createAsyncThunk(
  "membershipPlans/deleteMembershipPlan",
  async ({ id, payload }) => {
    try {
      const response = await api.membershipPlans.deleteMembershipPlan(id, payload);
      if (response.status === 200) {
        return response.data;
      }

      throw new Error(`Unexpected status code: ${response.status}`);
    } catch (error) {
      console.error("API call failed:", error);
      throw error;
    }
  }
);

const membershipPlansSlice = createSlice({
  name: "membershipPlans",
  initialState,
  reducers: {
    setMembershipPlan: (state, action) => {
      state.selectedMembershipPlan = action.payload;
      state.selectedMembershipPlanRecord = null;
    },
    setMembershipPlanRecord: (state, action) => {
      state.selectedMembershipPlanRecord = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllMembershipPlans.pending, (state) => {
        console.log("Fetching membership plans...");
      })
      .addCase(getAllMembershipPlans.fulfilled, (state, action) => {
        console.log("Membership plans fetched successfully:", action.payload);
        state.membershipPlans = normalizeMembershipPlans(action.payload);
        state.count = action.payload?.count ?? state.membershipPlans.length;
      })
      .addCase(getAllMembershipPlans.rejected, (state, action) => {
        state.membershipPlans = [];
        state.count = 0;
        console.log("Failed to fetch membership plans:", action.error.message);
      })
      .addCase(createMembershipPlan.fulfilled, (state, action) => {
        console.log("Membership plan created successfully", action.payload);
        if (Array.isArray(state.membershipPlans)) {
          state.membershipPlans.push(action.payload);
        } else {
          state.membershipPlans = [action.payload];
        }
        state.count = (state.count || 0) + 1;
      })
      .addCase(createMembershipPlan.pending, () => {
        console.log("Creating membership plan...");
      })
      .addCase(createMembershipPlan.rejected, (state, action) => {
        console.error("Failed to create membership plan", action.error.message);
      })
      .addCase(updateMembershipPlan.fulfilled, (state, action) => {
        const updatedMembershipPlan = action.payload;
        if (!Array.isArray(state.membershipPlans)) {
          return;
        }

        const updatedId = updatedMembershipPlan?.id ?? updatedMembershipPlan?.ID;
        state.membershipPlans = state.membershipPlans.map((item) =>
          (item?.id ?? item?.ID) === updatedId ? { ...item, ...updatedMembershipPlan } : item
        );
      })
      .addCase(deleteMembershipPlan.fulfilled, (state, action) => {
        const deletedId = action.meta.arg?.id;
        if (!Array.isArray(state.membershipPlans)) {
          return;
        }

        state.membershipPlans = state.membershipPlans.filter((item) => item.ID !== deletedId);
        state.count = Math.max((state.count || 1) - 1, 0);
      })
      .addCase(getMembershipPlanTenants.pending, () => {
        console.log("Fetching membership plan tenants...");
      })
      .addCase(getMembershipPlanTenants.fulfilled, (state, action) => {
        state.tenantsData = normalizeTenants(action.payload);
      })
      .addCase(getMembershipPlanTenants.rejected, (state, action) => {
        state.tenantsData = [];
        console.log("Failed to fetch membership plan tenants:", action.error.message);
      })
      .addCase(getMembershipPlanCountries.pending, () => {
        console.log("Fetching membership plan countries...");
      })
      .addCase(getMembershipPlanCountries.fulfilled, (state, action) => {
        state.countriesData = normalizeCountries(action.payload);
      })
      .addCase(getMembershipPlanCountries.rejected, (state, action) => {
        state.countriesData = [];
        console.log("Failed to fetch membership plan countries:", action.error.message);
      })
      .addCase(getMembershipPlanSecurityProviders.pending, () => {
        console.log("Fetching membership plan security providers...");
      })
      .addCase(getMembershipPlanSecurityProviders.fulfilled, (state, action) => {
        state.securityProvidersData = normalizeSecurityProviders(action.payload);
      })
      .addCase(getMembershipPlanSecurityProviders.rejected, (state, action) => {
        state.securityProvidersData = [];
        console.log("Failed to fetch membership plan security providers:", action.error.message);
      });
  },
});

export const { setMembershipPlan, setMembershipPlanRecord } = membershipPlansSlice.actions;

export default membershipPlansSlice.reducer;